import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { useConceptSearch } from "@/contexts/ConceptSearchContext";
import { routes } from "@/lib/routes";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import User from "lucide-react/dist/esm/icons/user";
import LogOut from "lucide-react/dist/esm/icons/log-out";
import Map from "lucide-react/dist/esm/icons/map";
import BookOpen from "lucide-react/dist/esm/icons/book-open";
import Home from "lucide-react/dist/esm/icons/home";
import Info from "lucide-react/dist/esm/icons/info";
import Menu from "lucide-react/dist/esm/icons/menu";
import Sun from "lucide-react/dist/esm/icons/sun";
import Moon from "lucide-react/dist/esm/icons/moon";
import Search from "lucide-react/dist/esm/icons/search";
import Trophy from "lucide-react/dist/esm/icons/trophy";

export function Header() {
  const { user, signOut } = useAuth();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { setOpen: openConceptSearch } = useConceptSearch();
  const isDark = resolvedTheme === "dark";
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async (): Promise<void> => {
    await signOut();
    navigate(routes.login);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <Logo />

        <nav className="hidden items-center gap-6 md:flex">
          <Link
            to={routes.home}
            className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <Home className="h-4 w-4" />
            Home
          </Link>
          <Link
            to={routes.mindMap}
            className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <Map className="h-4 w-4" />
            Mind Map
          </Link>
          <Link
            to={routes.problems}
            className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <BookOpen className="h-4 w-4" />
            Problems
          </Link>
          <Link
            to={routes.leaderboard}
            className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <Trophy className="h-4 w-4" />
            Leaderboard
          </Link>
          <Link
            to={routes.about}
            className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <Info className="h-4 w-4" />
            About
          </Link>
        </nav>

        <div className="flex shrink-0 items-center gap-2 md:gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 sm:min-h-11 sm:min-w-11"
            onClick={() => openConceptSearch(true)}
            aria-label="Search concepts (Ctrl+K)"
          >
            <Search className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 sm:min-h-11 sm:min-w-11"
            onClick={() => setTheme(isDark ? "light" : "dark")}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDark ? (
              <Sun className="h-4 w-4 sm:h-5 sm:w-5" />
            ) : (
              <Moon className="h-4 w-4 sm:h-5 sm:w-5" />
            )}
          </Button>
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9 sm:min-h-11 sm:min-w-11 md:hidden" aria-label="Open menu">
                <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-64 max-w-[85vw]">
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>
              <nav className="mt-6 flex flex-col gap-4">
                <Link
                  to={routes.home}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                >
                  <Home className="h-4 w-4" />
                  Home
                </Link>
                <Link
                  to={routes.mindMap}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                >
                  <Map className="h-4 w-4" />
                  Mind Map
                </Link>
                <Link
                  to={routes.problems}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                >
                  <BookOpen className="h-4 w-4" />
                  Problems
                </Link>
                <Link
                  to={routes.leaderboard}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                >
                  <Trophy className="h-4 w-4" />
                  Leaderboard
                </Link>
                <Link
                  to={routes.about}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                >
                  <Info className="h-4 w-4" />
                  About
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={user.user_metadata?.avatar_url} alt={user.email ?? ""} />
                    <AvatarFallback>
                      {user.email?.charAt(0).toUpperCase() ?? "U"}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-popover" align="end">
                <div className="flex items-center justify-start gap-2 p-2">
                  <div className="flex flex-col space-y-1 leading-none">
                    {user.user_metadata?.full_name && (
                      <p className="font-medium text-foreground">{user.user_metadata.full_name}</p>
                    )}
                    {user.email && (
                      <p className="w-[200px] truncate text-sm text-muted-foreground">
                        {user.email}
                      </p>
                    )}
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to={routes.profile} className="flex cursor-pointer items-center">
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild>
              <Link to={routes.login}>Sign in</Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
